import type { Subscription } from '@/types'

/**
 * Все ключевые состояния подписки для визуальной верификации SubscriptionSection.
 *
 * Каждый пресет описывает одно конкретное состояние стора. Кладётся в стор
 * целиком (`setSubscription(preset.subscription)`) — это симулирует то, что
 * раньше пришло бы с `/api/user/profile`. На реальный бэк не влияет.
 *
 * Пресет 4 рассчитан на провайдера с фиксированной ценой — для него мы
 * нарочно отдаём `renewalPrice: null`, чтобы UI ушёл в fallback на цену
 * из тарифа (из `usePlanLookup`).
 */
export interface SubscriptionPreset {
  id: string
  label: string
  description: string
  subscription: Subscription
}

const inThirtyDays = (): string =>
  new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()

export const SUBSCRIPTION_PRESETS: SubscriptionPreset[] = [
  {
    id: 'inactive',
    label: '1. Неактивна',
    description: 'Подписки нет — кнопка «Оформить подписку»',
    subscription: { isActive: false },
  },
  {
    id: 'robokassa-on-fixed',
    label: '2. Robokassa, on, закреплённая цена',
    description: 'autoRenew=true, есть renewalPrice — тариф из подписки',
    subscription: {
      isActive: true,
      expiresAt: inThirtyDays(),
      subscriptionInfoId: 1,
      autoRenew: true,
      provider: 'robokassa',
      renewalPrice: 990,
      renewalCurrency: 'RUB',
    },
  },
  {
    id: 'robokassa-on-fallback',
    label: '3. Robokassa, on, цена из тарифа',
    description: 'autoRenew=true, renewalPrice=null — fallback на usePlanLookup',
    subscription: {
      isActive: true,
      expiresAt: inThirtyDays(),
      subscriptionInfoId: 1,
      autoRenew: true,
      provider: 'robokassa',
      renewalPrice: null,
      renewalCurrency: null,
    },
  },
  {
    id: 'robokassa-off',
    label: '4. Robokassa, off',
    description: 'autoRenew=false — подписка дотечёт и закончится',
    subscription: {
      isActive: true,
      expiresAt: inThirtyDays(),
      subscriptionInfoId: 1,
      autoRenew: false,
      provider: 'robokassa',
      renewalPrice: 990,
      renewalCurrency: 'RUB',
    },
  },
  {
    id: 'tribute-on-eur',
    label: '5. Tribute, on (EUR)',
    description: 'autoRenew=true, евро — есть кнопка «Управлять подпиской»',
    subscription: {
      isActive: true,
      expiresAt: inThirtyDays(),
      subscriptionInfoId: 1,
      autoRenew: true,
      provider: 'tribute',
      renewalPrice: 19,
      renewalCurrency: 'EUR',
    },
  },
  {
    id: 'tribute-off',
    label: '6. Tribute, off',
    description: 'autoRenew=false — только дата + тариф, без кнопок',
    subscription: {
      isActive: true,
      expiresAt: inThirtyDays(),
      subscriptionInfoId: 1,
      autoRenew: false,
      provider: 'tribute',
      renewalPrice: 19,
      renewalCurrency: 'EUR',
    },
  },
  {
    id: 'legacy-on',
    label: '7. Legacy (provider=null), on',
    description: 'Старая подписка без provider — без тоггла, без кнопок',
    subscription: {
      isActive: true,
      expiresAt: inThirtyDays(),
      subscriptionInfoId: 1,
      autoRenew: true,
      provider: null,
      renewalPrice: null,
      renewalCurrency: null,
    },
  },
  {
    id: 'legacy-off',
    label: '8. Legacy (provider=null), off',
    description: 'Старая подписка, off — только дата',
    subscription: {
      isActive: true,
      expiresAt: inThirtyDays(),
      subscriptionInfoId: 1,
      autoRenew: false,
      provider: null,
    },
  },
]
